import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { success, error, confirm } from '../utils/alert';
import TransactionModal from '../components/TransactionModal';
import {
  ArrowLeft, CheckCircle2, Plus, Check,
  ArrowUp, ArrowDown, Pencil, X,
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH');
const fmtDate = (d) => new Date(d).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });

export default function MeDebtEdit() {
  const { id } = useParams();
  const [debt, setDebt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null, editId: null });

  const load = async () => {
    try {
      const res = await api.get(`/mydebt/${id}`);
      setDebt(res.data);
    } catch { error('ໂຫຼດຂໍ້ມູນບໍ່ໄດ້'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const openAdd = (defaultType = 'add') => setModal({ open: true, initial: { type: defaultType }, editId: null });
  const openEdit = (tx) => setModal({ open: true, initial: tx, editId: tx._id });

  const handleSave = async (form) => {
    try {
      if (modal.editId) {
        await api.put(`/mydebt/${id}/transaction/${modal.editId}`, form);
        success('ແກ້ໄຂແລ້ວ');
      } else {
        await api.post(`/mydebt/${id}/transaction`, form);
        success(form.type === 'add' ? 'ບັນທຶກໜີ້ແລ້ວ' : 'ບັນທຶກການຊຳລະແລ້ວ');
      }
      setModal({ open: false, initial: null, editId: null });
      load();
    } catch (err) { error(err.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ'); }
  };

  const handleDelete = async (txId) => {
    const res = await confirm('ລົບລາຍການນີ້?', '', 'ລົບ');
    if (!res.isConfirmed) return;
    try {
      await api.delete(`/mydebt/${id}/transaction/${txId}`);
      success('ລົບແລ້ວ');
      load();
    } catch { error('ລົບບໍ່ໄດ້'); }
  };

  if (loading) return <div className="flex justify-center pt-20"><div className="w-8 h-8 border-2 border-dark border-t-transparent rounded-full animate-spin" /></div>;
  if (!debt) return <div className="text-center pt-20 text-muted">ບໍ່ພົບຂໍ້ມູນ</div>;

  const totalDebt = debt.transactions?.reduce((s, t) => t.type === 'add' ? s + t.amount : s - t.amount, 0) || 0;
  const totalAdded = debt.transactions?.filter(t => t.type === 'add').reduce((s, t) => s + t.amount, 0) || 0;
  const totalPaid = debt.transactions?.filter(t => t.type === 'pay').reduce((s, t) => s + t.amount, 0) || 0;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="pt-2 flex items-center gap-3">
        <Link to="/mydebt" className="w-8 h-8 bg-white/70 rounded-xl flex items-center justify-center shadow-sm">
          <ArrowLeft size={16} className="text-dark" />
        </Link>
        <div>
          <h1 className="page-title">{debt.creditorName}</h1>
          {debt.phone && <p className="text-muted text-xs">{debt.phone}</p>}
        </div>
      </div>

      <div className="card bg-gradient-to-br from-red-900 to-red-800/80 border-0 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-red-200/50 text-xs">ຍອດທີ່ຕ້ອງຈ່າຍ</p>
            <p className={`text-2xl font-bold mt-0.5 ${totalDebt > 0 ? 'text-red-300' : 'text-green-400'}`}>
              {fmt(totalDebt)} KIP
            </p>
          </div>
          {totalDebt <= 0 && (
            <div className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-lg flex items-center gap-1">
              <CheckCircle2 size={12} /> ຊຳລະຄົບ
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/10">
          <div>
            <p className="text-red-200/40 text-xs">ໜີ້ລວມ</p>
            <p className="text-red-400 font-semibold text-sm">{fmt(totalAdded)} KIP</p>
          </div>
          <div>
            <p className="text-red-200/40 text-xs">ຊຳລະແລ້ວ</p>
            <p className="text-green-400 font-semibold text-sm">{fmt(totalPaid)} KIP</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => openAdd('add')} className="btn-danger py-3 text-sm flex items-center justify-center gap-1.5">
          <Plus size={15} /> ບັນທຶກໜີ້
        </button>
        <button onClick={() => openAdd('pay')} className="py-3 text-sm rounded-xl bg-green-500 text-white font-medium active:scale-95 transition-all flex items-center justify-center gap-1.5">
          <Check size={15} /> ຊຳລະໜີ້
        </button>
      </div>

      <div>
        <p className="text-xs font-medium text-muted mb-2">{debt.transactions?.length || 0} ລາຍການ</p>
        {!debt.transactions?.length ? (
          <div className="card text-center py-8"><p className="text-muted text-sm">ຍັງບໍ່ມີລາຍການ</p></div>
        ) : (
          <div className="space-y-2">
            {[...debt.transactions].reverse().map((tx, i) => (
              <div key={tx._id} className="card animate-slide-up" style={{ animationDelay: `${i * 30}ms` }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5
                      ${tx.type === 'add' ? 'bg-red-100 text-red-500' : 'bg-green-100 text-green-600'}`}>
                      {tx.type === 'add' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`font-bold text-sm ${tx.type === 'add' ? 'text-red-500' : 'text-green-600'}`}>
                          {tx.type === 'add' ? '+' : '-'}{fmt(tx.amount)} KIP
                        </span>
                        {tx.paymentMethod === 'cash'
                          ? <span className="badge-green text-xs">ເງິນສົດ</span>
                          : <span className="badge-rainbow">ໂອນ</span>}
                      </div>
                      {tx.note && <p className="text-xs text-muted truncate mt-0.5">{tx.note}</p>}
                      <p className="text-xs text-muted/60 mt-0.5">{fmtDate(tx.date)} • {tx.createdBy}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(tx)} className="w-7 h-7 bg-sand/30 rounded-lg flex items-center justify-center text-dark">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(tx._id)} className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center text-red-500">
                      <X size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TransactionModal
        open={modal.open}
        onClose={() => setModal({ open: false, initial: null, editId: null })}
        onSave={handleSave}
        initial={modal.initial}
        title={modal.editId ? 'ແກ້ໄຂລາຍການ' : 'ເພີ່ມລາຍການ'}
      />
    </div>
  );
}
